/* global debounce */

if (!customElements.get('slide-show')) {
  class SlideShow extends HTMLElement {
    constructor() {
      super();
      window.initLazyScript(this, this.init.bind(this));
    }

    init() {
      if (this.slides) return;
      this.slideshow = this.querySelector('.slideshow');
      this.slides = this.querySelectorAll('.slideshow__slide');
      this.nav = this.querySelector('.slideshow-nav');
      this.pagination = this.querySelector('.slideshow-pagination');
      this.rtl = document.dir === 'rtl';
      this.currentIndex = 0;
      this.swipeThreshold = 80;
      this.wheelSensitivity = 15;

      if (this.nav) {
        this.counterCurrent = this.querySelector('.slideshow-nav__counter-current');
        this.nav.addEventListener('click', this.handleNavClick.bind(this));
      }

      if (this.pagination) {
        this.pageBtns = this.querySelectorAll('.page-btn');
        this.pagination.addEventListener('click', this.handlePaginationClick.bind(this));
      }

      if (theme.device.hasTouch) {
        this.slideshow.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
        this.slideshow.addEventListener('touchmove', debounce(this.handleTouchMove.bind(this), 200), { passive: true });
      }

      this.slideshow.addEventListener('wheel', this.handleWheel.bind(this), { passive: false });

      this.slideshow.addEventListener('keydown', this.handleKeyDown.bind(this));

      this.slideshow.addEventListener('transitionend', SlideShow.handleTransitionend);

      this.setSlideVisibility();
      if (this.dataset.autoplay === 'true') this.initAutoPlay();
    }

    initAutoPlay() {
      this.autoplayEnabled = true;
      this.autoplaySpeed = this.dataset.speed * 1000;

      this.addEventListener('mouseover', this.pauseAutoplay.bind(this));
      this.addEventListener('mouseleave', this.resumeAutoplay.bind(this));
      this.slideshow.addEventListener('focusin', this.pauseAutoplay.bind(this));
      this.slideshow.addEventListener('focusout', this.resumeAutoplay.bind(this));
      this.visibilityHandler = () => {
        if (document.hidden) this.pauseAutoplay({ type: 'document' });
        else this.resumeAutoplay({ type: 'document' });
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      this.addObserver();
      this.startAutoplay();
    }

    /**
     * Handles 'click' events on the nav buttons container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (evt.target.matches('.autoplay-btn')) {
        this.togglePlayState(evt);
        return;
      }

      if (evt.target.name === 'next') {
        this.showNextSlide();
      } else if (evt.target.name === 'prev') {
        this.showPrevSlide();
      }
    }

    /**
     * Handles 'click' events on the pagination.
     * @param {object} evt - Event object.
     */
    handlePaginationClick(evt) {
      if (!evt.target.matches('.page-btn')) return;
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.setActiveSlide(Number(evt.target.dataset.index));
    }

    /**
     * Handles 'wheel' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleWheel(evt) {
      /*
        In theory, preventDefault on wheel events should stop scroll events entirely.
        This is the case, however a fast scroll will still register a deltaX value.
        This is useful as it allows us to prevent the scroll but still trigger the
        slide change on a prominent scroll. It shouldn't work - but it does?
      */
      if (evt.deltaX !== 0) evt.preventDefault();

      /*
        Consider throttling? timeout already applied to slide changes...
      */
      const isHorizontal = Math.abs(evt.deltaX) > Math.abs(evt.deltaY);
      const isHorizontalSignificant = Math.abs(evt.deltaX) > this.wheelSensitivity;

      if (isHorizontal && isHorizontalSignificant) {
        evt.deltaX > 0 ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'keyDown' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleKeyDown(evt) {
      if (evt.key === 'ArrowLeft' || evt.key === 'ArrowRight') {
        evt.preventDefault();
        if (evt.key === 'ArrowRight') {
          this.showNextSlide();
        }
        if (evt.key === 'ArrowLeft') {
          this.showPrevSlide();
        }
      }
    }

    /**
     * Handles 'touchstart' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchStart(evt) {
      this.touchStart = evt.changedTouches[0].screenX;
    }

    /**
     * Handles 'touchmove' events on the slideshow.
     * @param {object} evt - Event object.
     */
    handleTouchMove(evt) {
      const touchEnd = evt.changedTouches[0].screenX;

      if (touchEnd < this.touchStart - this.swipeThreshold) {
        this.rtl ? this.showPrevSlide() : this.showNextSlide();
      }

      if (touchEnd > this.touchStart + this.swipeThreshold) {
        this.rtl ? this.showNextSlide() : this.showPrevSlide();
      }
    }

    /**
     * Handles 'transitionend' events on the slideshow.
     * @param {object} evt - Event object.
     */
    static handleTransitionend(evt) {
      if (!evt.target.matches('.has-motion')) return;
      evt.target.classList.remove('transition-out');
    }

    showNextSlide() {
      let index = (this.requestedIndex ?? this.currentIndex) + 1;
      if (index === this.slides.length) index = 0;
      this.setActiveSlide(index, 'next');
    }

    showPrevSlide() {
      let index = (this.requestedIndex ?? this.currentIndex) - 1;
      if (index < 0) index = this.slides.length - 1;
      this.setActiveSlide(index, 'prev');
    }

    /**
     * Sets the active slide.
     * @param {number} slideIndex - Index of the slide to show.
     * @param {string} slideDirection - Direction for slide transitions.
     */
    async setActiveSlide(slideIndex, slideDirection) {
      if (!this.slides[slideIndex] || this.disconnected) return;
      const request = this.slideRequest = (this.slideRequest || 0) + 1;
      this.requestedIndex = slideIndex;
      this.mediaPending = true;
      this.stopAutoplay();
      this.slides[this.currentIndex].classList.remove('transition-out');

      // Cancel any prior calls to this that have not completed
      clearTimeout(this.setActiveSlideTimeoutId);

      // Clear autoplay timer
      clearTimeout(this.autoplayTimer);

      if (slideIndex === this.currentIndex) {
        this.requestedIndex = null;
        this.mediaPending = false;
        this.queueAutoplay(this.autoplaySpeed);
        return;
      }
      const ready = await this.prepareSlideMedia(this.slides[slideIndex]);
      if (request !== this.slideRequest || this.disconnected) return;
      this.mediaPending = false;
      if (!ready) {
        this.requestedIndex = null;
        return;
      }

      // Set data attribute for direction of slide transitions.
      let dir = slideDirection || (slideIndex < this.currentIndex ? 'prev' : 'next');
      if (this.rtl) dir = dir === 'next' ? 'prev' : 'next';
      this.dataset.direction = dir;

      // Trigger text overlay transition out.
      this.slides[this.currentIndex].classList.add('transition-out');

      this.setActiveSlideTimeoutId = setTimeout(() => {
        if (request !== this.slideRequest || this.disconnected) return;
        // Move new slide into the viewport.
        this.slideshow.scrollTo({ left: this.slides[slideIndex].offsetLeft, behavior: 'auto' });

        // Update current slide and trigger transitions.
        this.slides[slideIndex].classList.add('is-active');
        this.slides[this.currentIndex].classList.remove('is-active', 'transition-out');

        // Add class to transitioned content element (if it exists).
        const transitionedEl = this.slides[this.currentIndex].querySelector('.has-motion');
        if (transitionedEl) transitionedEl.classList.add('transition-out');

        // Reset autoplay slide timer.
        if (this.autoplayEnabled) this.autoplayStartTime = Date.now();

        // Update slide counter.
        if (this.counterCurrent) this.counterCurrent.textContent = slideIndex + 1;

        this.currentIndex = slideIndex;
        this.requestedIndex = null;
        this.setSlideVisibility();
        this.updatePagination();

        // Autoplay
        this.queueAutoplay(this.autoplaySpeed);
      }, 200);
    }

    /**
     * Materializes media kept inert in a template until its slide is requested.
     * @param {HTMLElement} slide - Slide whose image should become fetchable.
     * @returns {boolean} Whether deferred media was materialized.
     */
    loadSlideMedia(slide) {
      const template = slide?.querySelector('[data-slideshow-media-template]');
      if (!template) return false;

      template.replaceWith(template.content.cloneNode(true));
      return true;
    }

    async prepareSlideMedia(slide) {
      this.loadSlideMedia(slide);
      const images = Array.from(slide?.querySelectorAll('img') || []);
      const ready = await Promise.all(images.map((image) => this.waitForImage(image)));
      return ready.every(Boolean);
    }

    waitForImage(image) {
      this.mediaWaits ||= new WeakMap();
      this.mediaCleanups ||= new Set();
      if (this.mediaWaits.has(image)) return this.mediaWaits.get(image);
      image.loading = 'eager';
      const wait = new Promise((resolve) => {
        const finish = (loaded) => {
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          this.mediaCleanups.delete(cancel);
          resolve(loaded);
        };
        const onLoad = () => finish(image.naturalWidth > 0);
        const onError = () => finish(false);
        const cancel = () => finish(false);
        this.mediaCleanups.add(cancel);
        image.addEventListener('load', onLoad);
        image.addEventListener('error', onError);
        if (image.complete) onLoad();
      }).then(async (loaded) => {
        if (!loaded || this.disconnected) return false;
        try { await image.decode?.(); } catch {}
        return image.complete && image.naturalWidth > 0;
      }).finally(() => this.mediaWaits.delete(image));
      this.mediaWaits.set(image, wait);
      return wait;
    }

    /**
     * Updates the 'aria-current' attribute of the pagination buttons.
     */
    updatePagination() {
      if (!this.pagination) return;

      this.pageBtns.forEach((el, index) => {
        if (index === this.currentIndex) {
          el.setAttribute('aria-current', 'true');
        } else {
          el.removeAttribute('aria-current');
        }
      });
    }

    stopAutoplay() {
      this.setSlideshowState('paused');
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.autoplayRequest = (this.autoplayRequest || 0) + 1;
    }

    startAutoplay() {
      this.autoplayTimeLeft = this.autoplaySpeed;
      this.queueAutoplay(this.autoplaySpeed);
    }

    async queueAutoplay(delay = this.autoplaySpeed) {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;
      const request = this.autoplayRequest;
      const ready = await this.prepareSlideMedia(this.slides[this.currentIndex]);
      if (!ready || request !== this.autoplayRequest || !this.canAutoplay()) return;
      this.autoplayTimeLeft = Math.max(0, Math.min(delay, this.autoplaySpeed));
      this.autoplayStartTime = Date.now();
      this.autoplayTimer = setTimeout(this.showNextSlide.bind(this), this.autoplayTimeLeft);
      this.setSlideshowState('running');
    }

    canAutoplay() {
      return this.autoplayEnabled && !this.disconnected && !document.hidden
        && !this.mediaPending && !this.autoplayBlocks?.size;
    }

    autoplayReason(evt) {
      if (evt?.type === 'mouseover' || evt?.type === 'mouseleave') return 'hover';
      if (evt?.type === 'focusin' || evt?.type === 'focusout') return 'focus';
      return evt?.type || 'manual';
    }

    pauseAutoplay(evt) {
      this.autoplayBlocks ||= new Set();
      this.autoplayBlocks.add(this.autoplayReason(evt));
      if (this.autoplayTimer !== null && this.autoplayTimer !== undefined) {
        this.autoplayTimeLeft = Math.max(0, (this.autoplayTimeLeft ?? this.autoplaySpeed) - (Date.now() - this.autoplayStartTime));
      }
      this.stopAutoplay();
    }

    resumeAutoplay(evt) {
      if (evt?.type === 'focusout' && this.slideshow.contains(evt.relatedTarget)) return;
      this.autoplayBlocks?.delete(this.autoplayReason(evt));
      this.queueAutoplay(this.autoplayTimeLeft ?? this.autoplaySpeed);
    }

    /**
     * Adds an observer to pause autoplay when the slideshow is not in the viewport.
     */
    addObserver() {
      if ('IntersectionObserver' in window === false) return;

      this.pauseAutoplay({ type: 'viewport' });
      this.autoplayObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.resumeAutoplay({ type: 'viewport' });
          } else {
            this.pauseAutoplay({ type: 'viewport' });
          }
        });
      });

      this.autoplayObserver.observe(this);
    }

    connectedCallback() {
      this.disconnected = false;
      if (!this.slides) return;
      this.autoplayObserver?.observe(this);
      if (this.visibilityHandler) document.addEventListener('visibilitychange', this.visibilityHandler);
      this.resumeAutoplay({ type: 'viewport' });
    }

    disconnectedCallback() {
      this.disconnected = true;
      this.slideRequest = (this.slideRequest || 0) + 1;
      clearTimeout(this.setActiveSlideTimeoutId);
      if (this.slideshow) this.stopAutoplay();
      this.autoplayObserver?.disconnect();
      if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.mediaCleanups?.forEach((cancel) => cancel());
      this.requestedIndex = null;
      this.mediaPending = false;
    }

    /**
     * Sets 'aria-hidden' and 'tabindex' of the slides and text overlay buttons.
     */
    setSlideVisibility() {
      this.slides.forEach((el, index) => {
        const btns = el.querySelectorAll('a');

        if (index === this.currentIndex) {
          el.setAttribute('aria-hidden', 'false');
          el.removeAttribute('tabindex');

          btns.forEach((btn) => {
            btn.removeAttribute('tabindex');
          });
        } else {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');

          btns.forEach((btn) => {
            btn.setAttribute('tabindex', '-1');
          });
        }
      });
    }

    /**
     * Sets 'aria-live' state of the slideshow and 'play-state' of the pagination animation.
     * @param {string} state - State to set ('running' or 'paused').
     */
    setSlideshowState(state) {
      this.style.setProperty('--play-state', state);
      this.slideshow.setAttribute('aria-live', state === 'running' ? 'off' : 'polite');
      this.autoplayPaused = state === 'paused';
    }

    /**
     * Toggles the autoplay state.
     * @param {object} evt - Event object.
     */
    togglePlayState(evt) {
      evt.target.classList.toggle('is-paused');
      this.autoplayEnabled = !this.autoplayEnabled;
      this.dataset.autoplay = this.autoplayEnabled ? 'true' : 'false';
      this.autoplayEnabled ? this.startAutoplay() : this.stopAutoplay();
    }
  }

  customElements.define('slide-show', SlideShow);
}
